import React, { useState, useEffect } from 'react';
import { FormGroup, InputGroup, Button, HTMLTable } from "@blueprintjs/core";
import { AxiosInstance } from 'axios';

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next';

import { Account, Voucher, Environment, VoucherItem } from './Types'
import * as utils from "./utils";

interface EasySetupProps {
    svc: AxiosInstance,
    currentEnv?: Environment,
}

function EasySetup({ svc, currentEnv = {} as Environment }: EasySetupProps) {
    const { t, i18n } = useTranslation();
    const { register, handleSubmit, reset, setValue } = useForm<Voucher>()
    const [acList, setAcList] = useState([] as Account[])
    const [items, setItems] = useState([] as VoucherItem[])
    const [totals, setTotals] = useState([0,0,0,0,0,0])

    useEffect(() => {
        console.log("useEffect [svc, currentEnv]");
        (async () => {
            try {
                if ("envId" in currentEnv) {
                    const res = await svc.post('/accountlist',
                        {
                            by: "acCode",
                            start: "",
                            limit: 0,
                            backward: false,
                            filters: {
                                isDetailOnly: true,
                            }
                        });

                    let vchItems = [] as VoucherItem[]
                    (res.data.data as Account[]).map((item, i) => {
                        let voucherItem: VoucherItem = {
                            acCode: item.acCode,
                            acName: item.acName1,
                            dr: 0,
                            cr: 0,
                        }
                        register({name: `items[${i}].acCode`})
                        setValue(`items[${i}].acCode`, item.acCode)
                        register({name: `items[${i}].description1`})
                        setValue(`items[${i}].description1`, 'Account Setup')
                        vchItems.push(voucherItem)
                        return null
                    })
                    setItems(vchItems)
                }
            } catch (error) {
                utils.showError(error)
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [svc, currentEnv])

    const handleHotkey = (e: React.KeyboardEvent<HTMLFormElement>) => {
        //console.log(`ctrl:${e.ctrlKey} alt:${e.altKey} shift:${e.shiftKey} meta:${e.metaKey} key:${e.key} keyCode:${e.keyCode}`)

        // ctrl + s
        if (e.ctrlKey && e.keyCode === 83) {
            e.preventDefault()
            handleSubmit(onSubmit)()
        }
    }

    const onSubmit = (data: Voucher) => {
        data.dr = 0
        data.cr = 0
        for (let i = data.items.length-1; i >= 0; i--) {
            if (data.items[i] === undefined ||
                (data.items[i].acCode !== "C" && (data.items[i].dr - data.items[i].cr) === 0) ||
                (data.items[i].acCode === "C" && data.items[i].description1 === ""  && (data.items[i].dr - data.items[i].cr) === 0)) {
                //console.log("deleting", i)
                data.items.splice(i, 1)
            } else {
                data.dr += Number(data.items[i].dr)
                data.cr += Number(data.items[i].cr)
            }
        }
        console.log("data", data)
        console.log(data.dr !== 0, data.cr !== 0)
        if (data.dr === data.cr) {
            if (data.dr !== 0 && data.cr !== 0) {
                onSave(data)
            } else {
                utils.showError(t('data_missing'))
            }
        } else {
            console.log('out_of_balance')
            utils.showError(t('out_of_balance'))
        }
    }

    const onSave = async (vch: Voucher) => {
        try {
            const res = await svc.post('/voucheradd', vch)
            //console.log(vch)
            utils.showSuccess(t('saved', {key: res.data.data.journalCode+' '+res.data.data.voucherCode}))
        } catch (error) {
            utils.showError(error)
        }
    }

    return (
        <>
            <div style={{fontSize: "21px", fontWeight: 700}}>{t('easy_setup')}</div>
            <div className="Spacing-V-16"></div>
            <form onKeyDown={handleHotkey}>
                <div className="Row">
                    <Button className="Col-1-1" icon="tick" onClick={handleSubmit(onSubmit)}>{t('save')}</Button>
                </div>
                <div className="Spacing-V-16" />
                <div className="Row">
                    <FormGroup className="Col-1-1" label={t('journal')} labelFor="inputJournalCode">
                        <InputGroup id="inputJournalCode" name="journalCode" defaultValue="SU" inputRef={register} />
                    </FormGroup>
                    <FormGroup className="Col-2-1" label={t('voucher')} labelFor="inputVoucherCode">
                        <InputGroup id="inputVoucherCode" name="voucherCode" placeholder={t('auto_generated')} inputRef={register} />
                    </FormGroup>
                    <FormGroup className="Col-3-1" label={t('date')} labelFor="inputTxDate">
                        <InputGroup id="inputTxDate" name="txDate" type="date" defaultValue={utils.formatDate(new Date())} inputRef={register} />
                    </FormGroup>
                    <FormGroup className="Col-4-1" label={t('reference')} labelFor="inputReference">
                        <InputGroup id="inputReference" name="reference" inputRef={register} />
                    </FormGroup>
                </div>

                <HTMLTable striped interactive condensed>
                    <thead>
                        <tr>
                            <th className="TCol-ACCode">{t('account')}</th>
                            <th className="TCol-ACName">{t('account_name')}</th>
                            <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                            <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items && items.map((item: VoucherItem, i) =>
                            <tr key={item.acCode}>
                                <td className="TCol-ACCode">{item.acCode}</td>
                                <td className="TCol-ACName">{item.acName}</td>
                                <td className="TCol-ACBalance"><InputGroup id="inputDr" name={`items[${i}].dr`} type="number" inputRef={register} /></td>
                                <td className="TCol-ACBalance"><InputGroup id="inputCr" name={`items[${i}].cr`} type="number" inputRef={register} /></td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td className="TCol-ACCode"></td>
                            <td className="TCol-ACName"></td>
                            <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.drValue(totals[0])}</b></div></td>
                            <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.crValue(totals[1])}</b></div></td>
                        </tr>
                    </tfoot>
                </HTMLTable>
            </form>
        </>
    );
}

export default EasySetup